// Session persistence: the store's state in IndexedDB, so the app opens
// in its last state before the network answers. The state is plain
// JSON-safe data (ISO strings, a decimal sequence), which structured
// clone stores as it is. Saves are debounced and trailing — a burst of
// deltas writes once.
import type { SessionState } from "@/session/types";

export interface SessionPersistence {
  load(): Promise<SessionState | null>;
  save(state: SessionState): void;
  // Flush waits out the debounce, so a test (or a tab hiding) knows the
  // state is on disk.
  flush(): Promise<void>;
  clear(): Promise<void>;
}

// MemoryPersistence: for tests, and for browsers whose storage is full
// or blocked — a session then simply opens uncached.
export class MemoryPersistence implements SessionPersistence {
  saved: SessionState[] = [];
  private value: SessionState | null = null;
  private delayMs = 0;

  constructor(opts?: { delayMs?: number }) {
    this.delayMs = opts?.delayMs ?? 0;
  }

  preload(state: SessionState): void {
    this.value = state;
  }

  async load(): Promise<SessionState | null> {
    if (this.delayMs > 0) await new Promise((r) => setTimeout(r, this.delayMs));
    return this.value;
  }

  save(state: SessionState): void {
    this.saved.push(state);
    this.value = state;
  }

  async flush(): Promise<void> {}

  async clear(): Promise<void> {
    this.value = null;
  }
}

interface DbShape {
  db: IDBDatabase | null;
  err: Error | null;
}

function openDatabase(): Promise<DbShape> {
  return new Promise((resolve) => {
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open("psypen-session", 1);
    } catch (err) {
      resolve({ db: null, err: err instanceof Error ? err : new Error(String(err)) });
      return;
    }
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains("state")) req.result.createObjectStore("state");
    };
    req.onsuccess = () => resolve({ db: req.result, err: null });
    req.onerror = () => resolve({ db: null, err: req.error ?? new Error("indexedDB: open failed") });
  });
}

// IndexedDBPersistence: one row, "current", in one store, written a
// debounce after every save. A database that will not open degrades to
// saving nothing and loading nothing — never to a thrown error on the
// session's path.
export class IndexedDBPersistence implements SessionPersistence {
  private dbp: Promise<DbShape> | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pending: SessionState | null = null;
  private writes: Promise<unknown>[] = [];

  private db(): Promise<DbShape> {
    this.dbp ??= openDatabase();
    return this.dbp;
  }

  async load(): Promise<SessionState | null> {
    const { db, err } = await this.db();
    if (!db) {
      console.warn("session cache: unavailable:", err?.message);
      return null;
    }
    return new Promise((resolve) => {
      const tx = db.transaction("state", "readonly");
      const get = tx.objectStore("state").get("current");
      get.onsuccess = () => resolve((get.result as SessionState | undefined) ?? null);
      get.onerror = () => resolve(null);
    });
  }

  save(state: SessionState): void {
    this.pending = state;
    if (this.timer !== null) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.write();
    }, 200);
  }

  async flush(): Promise<void> {
    while (this.timer !== null || this.writes.length > 0) {
      if (this.timer !== null) {
        clearTimeout(this.timer);
        this.timer = null;
        void this.write();
      }
      const writes = this.writes;
      this.writes = [];
      await Promise.all(writes);
    }
  }

  async clear(): Promise<void> {
    const { db } = await this.db();
    if (!db) return;
    await new Promise<void>((resolve) => {
      const tx = db.transaction("state", "readwrite");
      tx.objectStore("state").delete("current");
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  }

  private write(): void {
    const state = this.pending;
    if (!state) return;
    this.pending = null;
    const p = this.db().then(({ db }) => {
      if (!db) return;
      return new Promise<void>((resolve) => {
        const tx = db.transaction("state", "readwrite");
        tx.objectStore("state").put(state, "current");
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
        tx.onabort = () => resolve();
      });
    });
    this.writes.push(p);
  }
}
